/* eslint-disable max-len */
import { load as cheerio } from 'cheerio';
import React from 'react';
import fastIsEqual from 'react-fast-compare';
import { classNames } from '../../../CodesThatsGottaGoSomewhereElse';
import { Fauxdown } from '../../../concepts/text/Fauxdown';
import { FormattingOptionItem } from '../../../concepts/text/FormattingOptionItem';
import { Rect } from '../../../platform/geom/Rect';
import { TextSettings, textAlignmentToFlexAlign, textAlignmentToTextAlign } from '../entities/TextConventions';
import { TextRuler } from './TextRuler';

const UrlRegex = /https?:\/\/(www\.)?[-a-zA-Z0-9@:%._\+~#=]{2,256}\.[a-z]{2,6}\b([-a-zA-Z0-9@:%_\+.~#?&//=]*)/g;
const toAnchor = url => `<a style="text-decoration: underline;" href="${url.replace(/\.$/, '')}">${url}</a>`;

const formatMargin = (value: number | number[]) =>
    Array.isArray(value) ? value.map(x => x + 'px').join(' ') : value + 'px';

const charMap = new Map<string, string>();

const scramble = (str: string) => {
    // Simple hash function to generate a consistent scrambled character
    const hashChar = (char: string) => {
        const code = char.charCodeAt(0);
        return String.fromCharCode(((code * 1664525 + 1013904223) % 26) + 97);
    };

    const $ = cheerio(str, null, false);

    // Replace any <br /> with a non-HTML version
    $('br').each((_, elmt) => {
        $(elmt).replaceWith('[!br!]');
    });

    // Convert to text, replacing the br token with a new line
    const text = $.text().replaceAll('[!br!]', '\n');

    return text.trim().replace(/\S/g, (char) => {
        if (!charMap.has(char)) {
            charMap.set(char, hashChar(char));
        }
        return charMap.get(char)!;
    });
};

const resolveFontSize = (props: RenderTextSimpleProps) => {
    if (!!props.fontSize) return props.fontSize;
    return Math.min(props.maxFontSize ?? 1024, TextRuler.fit(
        props.value,
        props,
        props.bounds.width,
        props.bounds.height,// hasWidgets ? 92 : 95,
    ));
};

type RenderTextSimpleProps = TextSettings & {
    readonly bounds: Rect,
    readonly value: string,
    readonly formatOpts: FormattingOptionItem[],
    customize?(props: any): any,
};

const RenderTextSimpleUnoptimized = (props: RenderTextSimpleProps) => {
    const fontSize = resolveFontSize(props);
    const { customize, ...text } = props;
    const initial: any = {
        display: 'flex',
        position: 'absolute',
        left: text.bounds.left + 'px',
        top: text.bounds.top + 'px',
        width: text.bounds.width + 'px',
        height: text.bounds.height + 'px',
        color: text.fontColor,
        fontFamily: text.fontFamily,
        fontSize: fontSize + 'px',
        fontWeight: text.fontWeight,
        lineHeight: (fontSize * text.lineHeight) + 'px',
        padding: formatMargin(text.textMargin),
        whiteSpace: 'pre-wrap',
        overflowWrap: 'anywhere',
        boxSizing: 'border-box',
        overflow: 'hidden',
        textAlign: textAlignmentToTextAlign(text.textAlignH),
        justifyContent: textAlignmentToFlexAlign(text.textAlignH),
        alignItems: textAlignmentToFlexAlign(text.textAlignV),
    };
    const final = !!customize ? { ...initial, ...customize(initial) } : initial;
    const parsed = Fauxdown.toEditorForm((text.value ?? ''), text.formatOpts).replace(UrlRegex, toAnchor);
    const padded = parsed.match(/\n$/) ? parsed + ' ' : parsed;
    const maybeScrambled = text.fontFamily === 'redacted_scriptregular' ? scramble(padded) : padded;
    // eslint-disable-next-line no-underscore-dangle
    const __html = maybeScrambled;
    return <div {...classNames('ql-editor', text.fontFamily)} style={final}>
        <span dangerouslySetInnerHTML={{ __html }}></span>
    </div>;
};

const RenderTextSimple = React.memo(RenderTextSimpleUnoptimized, fastIsEqual);

export type RenderTextProps = {
    readonly settings: TextSettings,
    readonly bounds: Rect,
    readonly value: string,
    readonly formatOpts?: FormattingOptionItem[],
    customize?(props: any): any,
};

export const RenderText = ({ settings, ...props }: RenderTextProps) => {
    if (props.bounds.area() <= 0) return null;
    const merged = { formatOpts: [], ...settings, ...props };
    return <RenderTextSimple {...merged} />;
};
